import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { map, combineLatestWith, Observable, distinctUntilChanged, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { StateService } from '@uirouter/angular';

import { DialogService } from 'rev-shared/ui/dialog/Dialog.Service';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { IRules } from 'rev-shared/ui/css/CssRules.Contract';
import { isSafari, isApple } from 'rev-shared/util/UserAgentUtil';

import { ThemeService } from 'rev-portal/branding/Theme.Service';
import { WebcastModel } from 'rev-portal/scheduledEvents/webcast/model/WebcastModel';

import { AudioCueService } from './AudioCue.Service';
import { IProducerStreamsCfg } from './Contract';
import { ProducerService, stopProducerStreams } from './Producer.Service';

import styles from './ProducerWebcast.module.less';

@Component({
	selector: 'producer-view',
	templateUrl: './ProducerView.Component.html',
	host: { '[class]': 'rootCls' },
	standalone: false
})
export class ProducerViewComponent implements OnDestroy, OnInit {
	@Input() public webcast: WebcastModel;
	public streams: IProducerStreamsCfg;
	public ready: boolean;
	public isReloading: boolean;

	public styles = styles;
	public themeStyles: Observable<IRules>;
	private sub: Subscription;

	public isSafari = isSafari();
	public rootCls = [styles.root, isApple() ? 'ua-apple' : ''].join(' ');

	constructor(
		private readonly DialogService: DialogService,
		private readonly ProducerService: ProducerService,
		private readonly ThemeService: ThemeService,
		private readonly TranslateService: TranslateService,
		private readonly UserContext: UserContextService,
		private readonly State: StateService,
		private readonly AudioCueService: AudioCueService
	) {}

	public ngOnInit(): void {
		this.AudioCueService.init()
			.catch(e => {
				console.error('error loading audio cue: ', e);
			});

		this.ProducerService.init(this.webcast)
			.catch(e => {
				console.error('Error initializing producer service: ', e);
			});

		document.body.classList.add(styles.bodyFullHeight);
		if (this.isSafari) {
			const title = this.firstName ? 'Event_Producer_Setup_HelloUser' : 'Event_Producer_Setup_Hello';
			const dialogResult = this.DialogService.openStorysetDialog(
				this.TranslateService.instant(title, { '0' : this.firstName }),
				this.TranslateService.instant('Event_Producer_Setup_SafariWarning'),
				this.TranslateService.instant('Continue'),
				'/img/browser-warning.png'
			).result;
		}

		this.themeStyles = this.ThemeService.brandingSettings$.pipe(
			combineLatestWith(this.webcast.webcast$),
			map(() => ({
				':root': {
					'--producer-banner-accent': this.webcast.producerOptions.banner?.accentColor ||
						this.ThemeService.accentColor
				}
			}))
		);

		this.sub = this.webcast.webcast$.pipe(
			map(w => w.currentUser.isAssignedEventAdmin),
			distinctUntilChanged()
		).subscribe(() => {
			if(!this.ready) {
				return;
			}
			this.isReloading = true;
			this.State.reload();
		});
	}

	public ngOnDestroy(): void {
		if(this.streams) {
			stopProducerStreams(this.streams);
		}
		this.ProducerService.stop();

		document.body.classList.remove(styles.bodyFullHeight);

		this.sub?.unsubscribe();
		this.AudioCueService.stop();
	}

	public updateStreams(streams: IProducerStreamsCfg): void {
		this.streams = streams;
	}

	public goBackstage(): void {
		this.ready = true;
	}

	public closeSafariWarning(): void {
		this.isSafari = false;
	}

	public get fullName(): string {
		return this.UserContext.getUser().fullName;
	}

	public get firstName(): string {
		return this.UserContext.getUser().firstName;
	}
}
